import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useAccounts } from "@/api/accounts";
import { shouldShowRemaining, useAllowance } from "@/api/allowance";
import { downloadExport } from "@/api/export";
import { useTransactions } from "@/api/transactions";
import { Amount } from "@/components/Amount";
import {
  EmptyState,
  ErrorNote,
  Loading,
  PageTitle,
  buttonClass,
  linkClass,
  secondaryButtonClass,
} from "@/components/ui";
import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";

/**
 * The ledger.
 *
 * Newest first, and paged by cursor rather than offset: a ledger grows at the
 * end a reader starts from, so an offset page two would repeat or skip rows as
 * soon as anything is recorded between the two requests
 * (`architecture-guide.md` §4.11).
 */
export function TransactionsPage() {
  const t = useT();
  const format = useFormat();
  const { householdId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const accountId = params.get("account_id") ?? undefined;

  const accounts = useAccounts(householdId);
  const allowance = useAllowance(householdId);
  const filter = accountId ? { accountId } : {};
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTransactions(householdId, filter);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<unknown>();

  const rows = data?.pages.flatMap((page) => page.data) ?? [];
  const byAccount = new Map(accounts.data?.data.map((a) => [a.id, a]) ?? []);
  const hasAccounts = (accounts.data?.data.length ?? 0) > 0;

  return (
    <div>
      <PageTitle
        action={
          hasAccounts ? (
            <div className="flex items-center gap-2">
              {/*
                Offered only with two accounts, because that is the shortest way
                to say what a transfer is: money moving between two of your own.
              */}
              {(accounts.data?.data.length ?? 0) > 1 && (
                <Link
                  to={`/households/${householdId}/transfers/new`}
                  className={secondaryButtonClass}
                >
                  {t("transactions.transfer")}
                </Link>
              )}
              {/*
                The export honours the account filter above, because a person
                who has narrowed the screen to one account and then exports
                means that account. A button that quietly widened the answer
                back out would be the kind of surprise a finance product does
                not get to have.
              */}
              <button
                type="button"
                disabled={exporting}
                onClick={() => {
                  setExportError(undefined);
                  setExporting(true);
                  downloadExport(householdId, accountId ? { accountId } : {})
                    .catch(setExportError)
                    .finally(() => setExporting(false));
                }}
                className={secondaryButtonClass}
                data-testid="export-csv"
              >
                {exporting ? t("transactions.exporting") : "CSV"}
              </button>
              <Link
                to={`/households/${householdId}/transactions/new`}
                className={buttonClass}
              >
                {t("transactions.add")}
              </Link>
            </div>
          ) : undefined
        }
      >
        {t("nav.transactions")}
      </PageTitle>

      {hasAccounts && (
        <div className="mt-4">
          <label htmlFor="filter-account" className="sr-only">
            {t("transactions.filterByAccount")}
          </label>
          <select
            id="filter-account"
            value={accountId ?? ""}
            onChange={(e) => {
              const next = e.target.value;
              setParams(next ? { account_id: next } : {});
            }}
            className="min-h-11 rounded-control border border-line bg-surface px-3 py-1.5 text-sm text-ink transition-colors hover:border-ink/30 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25"
          >
            <option value="">{t("transactions.allAccounts")}</option>
            {accounts.data?.data.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {shouldShowRemaining(allowance.data) && (
        // The count appears only near the limit. project.md §5.5 wants the
        // interface calm, and "50 of 50 left" is a nag about something nobody
        // is close to.
        <p className="mt-3 text-sm text-muted" data-testid="allowance">
          {allowance.data?.transactions_remaining === 0
            ? t("allowance.usedUp")
            : t("allowance.remaining", {
                n: allowance.data?.transactions_remaining ?? 0,
              })}
        </p>
      )}

      {isLoading && <Loading />}
      <ErrorNote error={error} />
      <ErrorNote error={exportError} />

      {!isLoading && !hasAccounts && (
        <EmptyState title={t("accounts.emptyTitle")}>
          {t("transactions.needAnAccount")}{" "}
          <Link to={`/households/${householdId}/accounts/new`} className={linkClass}>
            {t("accounts.add")}
          </Link>
        </EmptyState>
      )}

      {!isLoading && hasAccounts && rows.length === 0 && (
        <EmptyState title={t("transactions.emptyTitle")}>
          {t("transactions.emptyBody")}
        </EmptyState>
      )}

      {rows.length > 0 && (
        <table className="mt-6 w-full text-sm">
          <thead className="text-xs font-medium text-muted">
            <tr className="border-b border-line">
              <th className="py-2 pr-3 text-left">{t("transactions.date")}</th>
              <th className="py-2 pr-3 text-left">{t("transactions.payee")}</th>
              {/* The account is the column a phone can spare: the filter above
                  already says which one is shown. */}
              <th className="hidden py-2 pr-3 text-left sm:table-cell">
                {t("transactions.account")}
              </th>
              <th className="py-2 text-right">{t("transactions.amount")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((tx) => (
              <tr key={tx.id}>
                <td className="py-2.5 pr-3 whitespace-nowrap tabular-nums text-ink">
                  {format.date(tx.booked_date)}
                </td>
                <td className="py-2.5 pr-3 text-ink">
                  {/*
                    A transfer leg has no payee and never will: the counterparty
                    is the household's own other account. Naming the operation is
                    more useful than an em dash.
                  */}
                  {tx.transfer_group_id ? (
                    <span className="text-muted">{t("transactions.transfer")}</span>
                  ) : (
                    tx.payee_raw || <span className="text-muted">—</span>
                  )}
                  {tx.note && (
                    <span className="ml-2 text-xs text-muted">
                      {tx.note}
                    </span>
                  )}
                </td>
                <td className="hidden py-2.5 pr-3 text-muted sm:table-cell">
                  {byAccount.get(tx.account_id)?.name ?? "—"}
                </td>
                <td className="py-2.5">
                  <Amount
                    minor={tx.money.amount_minor}
                    currency={tx.money.currency_code}
                    className="block"
                  />
                  {/*
                    model.md §6.3's receipt line: what was actually spent, under
                    what the account was charged. It is the smaller of the two
                    because the booked figure is the one every balance and budget
                    is built from — the original amount explains it rather than
                    competing with it.
                  */}
                  {tx.money.original_amount_minor !== undefined &&
                    tx.money.original_currency_code && (
                      <Amount
                        minor={tx.money.original_amount_minor}
                        currency={tx.money.original_currency_code}
                        className="block text-xs text-muted"
                        testId="original-amount"
                      />
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {hasNextPage && (
        <button
          type="button"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className={`${secondaryButtonClass} mt-6`}
        >
          {isFetchingNextPage ? t("common.loading") : t("common.loadMore")}
        </button>
      )}
    </div>
  );
}
